import React from 'react';
import { useTranslation } from 'react-i18next';

interface CustomDateTimePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean | string;
  style?: React.CSSProperties;
  className?: string;
  // New props for start/end validation
  isEndTime?: boolean;
  startDateTime?: Date | null;
  minGapMinutes?: number; // Minimum gap between start and end time
}

const CustomDateTimePicker: React.FC<CustomDateTimePickerProps> = ({ 
  value, 
  onChange, 
  label, 
  placeholder,
  disabled = false,
  required = false,
  error = false,
  style = {},
  className = "",
  isEndTime = false,
  startDateTime = null,
  minGapMinutes = 30 // Default 30-minute minimum gap
}) => {
  const { t, i18n } = useTranslation();
  
  // Use localized placeholder or provided one
  const localizedPlaceholder = placeholder || (i18n as any).t('dateTimePicker.placeholders.selectDateTime');

  // Define the time option interface
  interface TimeOption {
    value: string;
    display: string;
    disabled?: boolean;
  }

  // Generate time options in 30-minute intervals
  const generateTimeOptions = (): TimeOption[] => {
    const options: TimeOption[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = timeString; // Use 24-hour format directly
        options.push({ value: timeString, display: displayTime });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Check if a time option should be disabled based on start time
  const isTimeOptionDisabled = (timeValue: string, selectedDate: string) => {
    if (!isEndTime || !startDateTime) return false;
    
    // Parse the selected date and time
    const [hours, minutes] = timeValue.split(':').map(Number);
    const testDateTime = new Date(selectedDate);
    testDateTime.setHours(hours, minutes, 0, 0);
    
    // Calculate minimum allowed end time (start time + gap)
    const minEndTime = new Date(startDateTime.getTime() + (minGapMinutes * 60 * 1000));
    
    // Simply compare the actual datetime values
    // This automatically handles cross-midnight scenarios correctly
    return testDateTime < minEndTime;
  };

  // Get the minimum date for end time picker
  const getMinDate = () => {
    if (!isEndTime || !startDateTime) return '';
    return formatDateForInput(startDateTime);
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date | null) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format time for select (HH:MM)
  const formatTimeForSelect = (date: Date | null) => {
    if (!date) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Get current date string for default when no date is selected
  const getCurrentDate = () => {
    const today = new Date();
    return formatDateForInput(today);
  };

  // Validate if the selected datetime is valid
  const validateDateTime = (newDate: Date) => {
    if (!isEndTime || !startDateTime) return true;
    
    const minEndTime = new Date(startDateTime.getTime() + (minGapMinutes * 60 * 1000));
    return newDate >= minEndTime;
  };

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = e.target.value;
    if (!dateValue) {
      onChange(null);
      return;
    }
    
    const currentTime = value ? formatTimeForSelect(value) : '09:00';
    const [hours, minutes] = currentTime.split(':');
    const newDate = new Date(dateValue);
    newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // If this is an end time picker and the new date/time is invalid, 
    // set it to the minimum valid time
    if (isEndTime && startDateTime && !validateDateTime(newDate)) {
      const minEndTime = new Date(startDateTime.getTime() + (minGapMinutes * 60 * 1000));
      onChange(minEndTime);
    } else {
      onChange(newDate);
    }
  };

  // Handle time change
  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const timeValue = e.target.value;
    if (!timeValue) return;
    
    const currentDate = value ? formatDateForInput(value) : getCurrentDate();
    const [hours, minutes] = timeValue.split(':');
    const newDate = new Date(currentDate);
    newDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    
    // Validate the new datetime
    if (validateDateTime(newDate)) {
      onChange(newDate);
    }
  };

  // Clear the datetime value
  const handleClear = () => {
    onChange(null);
  };

  // Get formatted display value for current selection
  const getDisplayValue = () => {
    if (!value) return '';
    const dateStr = value.toLocaleDateString();
    const timeStr = formatTimeForSelect(value);
    const timeOption = timeOptions.find(opt => opt.value === timeStr);
    return `${dateStr} ${(i18n as any).t('dateTimePicker.labels.at')} ${timeOption?.display || timeStr}`;
  };

  // Get filtered time options based on selected date
  const getFilteredTimeOptions = (): TimeOption[] => {
    const baseOptions = timeOptions;
    
    if (!isEndTime || !startDateTime) return baseOptions;
    
    const selectedDate = formatDateForInput(value) || getCurrentDate();
    
    return baseOptions.map(option => ({
      ...option,
      disabled: isTimeOptionDisabled(option.value, selectedDate)
    }));
  };

  // Base input style
  const inputStyle = {
    padding: '0.5rem',
    border: error ? '1px solid #dc3545' : '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: disabled ? '#f8f9fa' : 'white',
    color: disabled ? '#6c757d' : 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    ...style
  };

  const focusStyle = {
    borderColor: '#007bff',
    boxShadow: '0 0 0 0.2rem rgba(0, 123, 255, 0.25)'
  };

  const filteredTimeOptions = getFilteredTimeOptions();

  return (
    <div className={`custom-datetime-picker ${className}`} style={{ width: '100%' }}>
      {label && (
        <label style={{ 
          display: 'block', 
          marginBottom: '0.25rem', 
          fontWeight: 'bold',
          color: error ? '#dc3545' : 'inherit'
        }}>
          {label} {required && <span style={{ color: '#dc3545' }}>*</span>}
        </label>
      )}
      
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
        {/* Date Input */}
        <input
          type="date"
          value={formatDateForInput(value)}
          onChange={handleDateChange}
          disabled={disabled}
          min={getMinDate()}
          style={{
            ...inputStyle,
            flex: '1',
            minWidth: '140px'
          }}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            Object.assign(target.style, focusStyle);
          }}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            const target = e.target as HTMLInputElement;
            Object.assign(target.style, { borderColor: error ? '#dc3545' : '#ced4da', boxShadow: 'none' });
          }}
        />
        
        {/* Time Select */}
        <select
          value={formatTimeForSelect(value)}
          onChange={handleTimeChange}
          disabled={disabled}
          style={{
            ...inputStyle,
            flex: '1',
            minWidth: '120px',
            cursor: disabled ? 'not-allowed' : 'pointer'
          }}
          onFocus={(e: React.FocusEvent<HTMLSelectElement>) => {
            const target = e.target as HTMLSelectElement;
            Object.assign(target.style, focusStyle);
          }}
          onBlur={(e: React.FocusEvent<HTMLSelectElement>) => {
            const target = e.target as HTMLSelectElement;
            Object.assign(target.style, { borderColor: error ? '#dc3545' : '#ced4da', boxShadow: 'none' });
          }}
        >
          <option value="">{(i18n as any).t('dateTimePicker.placeholders.selectTime')}</option>
          {filteredTimeOptions.map(option => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
              style={{
                color: option.disabled ? '#6c757d' : 'inherit',
                backgroundColor: option.disabled ? '#f8f9fa' : 'white'
              }}
            >
              {option.display}
            </option>
          ))}
        </select>
        
        {/* Clear Button */}
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            style={{
              padding: '0.5rem',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              color: '#6c757d',
              cursor: 'pointer',
              fontSize: '14px',
              minWidth: '60px',
              transition: 'all 0.15s ease-in-out'
            }}
            onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#e9ecef';
              target.style.borderColor = '#adb5bd';
            }}
            onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#f8f9fa';
              target.style.borderColor = '#ced4da';
            }}
            title={(i18n as any).t('dateTimePicker.tooltips.clearSelection')}
          >
            {(i18n as any).t('dateTimePicker.buttons.clear')}
          </button>
        )}
      </div>
      
      {/* Display current selection */}
      {value && (
        <div style={{
          marginTop: '0.25rem',
          fontSize: '12px',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          {(i18n as any).t('dateTimePicker.labels.selected')}: {getDisplayValue()}
        </div>
      )}
      
      {/* Validation warning for end time */}
      {isEndTime && startDateTime && (
        <div style={{
          marginTop: '0.25rem',
          fontSize: '11px',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          {(i18n as any).t('dateTimePicker.validation.endTimeMinGap', { minutes: minGapMinutes })}
        </div>
      )}
      
      {/* Error message */}
      {error && typeof error === 'string' && (
        <div style={{
          marginTop: '0.25rem',
          fontSize: '12px',
          color: '#dc3545'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default CustomDateTimePicker;

// Example usage component showing how to use start/end validation
export const DateTimeRangePicker: React.FC = () => {
  const { i18n } = useTranslation();
  const [startTime, setStartTime] = React.useState<Date | null>(null);
  const [endTime, setEndTime] = React.useState<Date | null>(null);

  return (
    <div style={{ padding: '20px', maxWidth: '600px' }}>
      <h3>{(i18n as any).t('dateTimeRangePicker.title')}</h3>
      
      <div style={{ marginBottom: '20px' }}>
        <CustomDateTimePicker
          value={startTime}
          onChange={setStartTime}
          label={(i18n as any).t('dateTimeRangePicker.labels.startTime')}
          required
        />
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <CustomDateTimePicker
          value={endTime}
          onChange={setEndTime}
          label={(i18n as any).t('dateTimeRangePicker.labels.endTime')}
          required
          isEndTime={true}
          startDateTime={startTime}
          minGapMinutes={30} // 30-minute minimum gap
        />
      </div>
      
      {startTime && endTime && (
        <div style={{
          padding: '10px',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724'
        }}>
          <strong>{(i18n as any).t('dateTimeRangePicker.labels.eventDuration')}:</strong> {
            Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60))
          } {(i18n as any).t('dateTimeRangePicker.units.minutes')}
        </div>
      )}
    </div>
  );
};